import classNames from "classnames";
import {useMemo, useState} from "react";
import {useFormContext} from "react-hook-form";

import {NavTabs} from "@/components/navTabs/NavTabs.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {CheckTypeDisplay, MonitoringServiceType} from "@/lib/types/checks.ts";
import {ANALYZER_CHECK_TYPE_TAB_ALL} from "@/pages/analyzer/Analyzer.constants.ts";
import {AnalyzerCreateFormValues, AnalyzerPhase} from "@/pages/analyzer/Analyzer.types.ts";
import {resolveContactGroupNames} from "@/pages/analyzer/Analyzer.utils.ts";
import {AnalyzerChecksTable} from "@/pages/analyzer/components/AnalyzerChecksTable.tsx";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";

import styles from "./AnalyzerChecksForm.module.scss";

export const AnalyzerChecksForm = () => {
  const {phase, proposedChecks, orderedChecks, awaitingProposals, limitsLoading, formData} = useAnalyzerContext();
  const {watch} = useFormContext<AnalyzerCreateFormValues>();
  const formTags = watch("tags");
  const contactGroups = watch("contactGroups");
  const [activeType, setActiveType] = useState(ANALYZER_CHECK_TYPE_TAB_ALL);
  const [search, setSearch] = useState("");
  const isRevising = phase === AnalyzerPhase.STREAMING && proposedChecks.length > 0;

  const typeCounts = useMemo(() => {
    const counts = new Map<MonitoringServiceType, number>();
    for (const {monitoring_service_type} of orderedChecks) {
      counts.set(monitoring_service_type, (counts.get(monitoring_service_type) ?? 0) + 1);
    }
    return counts;
  }, [orderedChecks]);
  const currentType = typeCounts.has(activeType as MonitoringServiceType) ? activeType : ANALYZER_CHECK_TYPE_TAB_ALL;

  const tabItems = useMemo(() => {
    if (awaitingProposals) {
      return [{id: ANALYZER_CHECK_TYPE_TAB_ALL, title: "All", badgeText: "—"}];
    }
    return [
      {id: ANALYZER_CHECK_TYPE_TAB_ALL, title: "All", badgeText: orderedChecks.length},
      ...[...typeCounts].map(([type, count]) => ({
        id: type as string,
        title: CheckTypeDisplay[type],
        badgeText: count,
      })),
    ];
  }, [awaitingProposals, orderedChecks.length, typeCounts]);

  const filteredChecks = useMemo(() => {
    const query = search.trim().toLowerCase();
    const formTagNames = formTags.map(({tag}) => tag);
    const formContactNames = resolveContactGroupNames(contactGroups, formData?.contactGroups.items ?? []);
    return orderedChecks.filter((check) => {
      if (currentType !== ANALYZER_CHECK_TYPE_TAB_ALL && check.monitoring_service_type !== currentType) {
        return false;
      }
      if (!query) {
        return true;
      }
      const searchFields = [
        check.name,
        check.msp_address,
        check.justification,
        CheckTypeDisplay[check.monitoring_service_type],
        ...(check.tags ?? formTagNames),
        ...(check.contact_groups ?? formContactNames),
      ];
      return searchFields.some((value) => value.toLowerCase().includes(query));
    });
  }, [orderedChecks, currentType, search, formTags, contactGroups, formData]);

  if (limitsLoading) {
    return <LoadingContainer />;
  }

  return (
    <div className={styles.checksForm}>
      <div className={styles.typeTabs}>
        <NavTabs
          variant="compact"
          className={styles.typeTabsNav}
          items={tabItems}
          selectedId={currentType}
          selectTab={setActiveType}
        />
      </div>
      <div className={styles.searchRow}>
        <SearchInput size="xsmall" value={search} onChange={setSearch} disabled={awaitingProposals} />
      </div>
      <div className={classNames(styles.tableArea, {[styles.revising]: isRevising})}>
        <AnalyzerChecksTable
          checks={filteredChecks}
          hideEmptyOptionalColumns={currentType !== ANALYZER_CHECK_TYPE_TAB_ALL}
        />
      </div>
    </div>
  );
};
